/**
 * Reusable Resend Timer Hook
 *
 * Provides countdown timer functionality for resend OTP operations.
 * Eliminates duplicate timer management code across components.
 *
 * Features:
 * - Auto-cleanup on unmount
 * - Prevents multiple timers from running simultaneously
 * - Configurable duration (default: 30 seconds)
 */

import { useState, useRef, useEffect, useCallback } from 'react'

interface UseResendTimerOptions {
    /**
     * Timer duration in seconds
     * @default 30
     */
    duration?: number
}

interface UseResendTimerReturn {
    /**
     * Current countdown value (0 means timer is not running)
     */
    timer: number

    /**
     * Whether the timer is currently running
     */
    isRunning: boolean

    /**
     * Start or restart the timer
     */
    start: () => void

    /**
     * Stop the timer manually
     */
    stop: () => void

    /**
     * Reset the timer to 0 without starting
     */
    reset: () => void
}

export function useResendTimer(options: UseResendTimerOptions = {}): UseResendTimerReturn {
    const { duration = 30 } = options

    const [timer, setTimer] = useState(0)
    const timerRef = useRef<NodeJS.Timeout | null>(null)

    // Stop timer
    const stop = useCallback(() => {
        if (timerRef.current) {
            clearInterval(timerRef.current)
            timerRef.current = null
        }
        setTimer(0)
    }, [])

    // Reset timer without starting
    const reset = useCallback(() => {
        if (timerRef.current) {
            clearInterval(timerRef.current)
            timerRef.current = null
        }
        setTimer(0)
    }, [])

    // Start timer
    const start = useCallback(() => {
        // Clear any existing timer
        if (timerRef.current) {
            clearInterval(timerRef.current)
            timerRef.current = null
        }

        // Set initial value
        setTimer(duration)

        // Start countdown
        timerRef.current = setInterval(() => {
            setTimer(prev => {
                if (prev <= 1) {
                    if (timerRef.current) {
                        clearInterval(timerRef.current)
                        timerRef.current = null
                    }
                    return 0
                }
                return prev - 1
            })
        }, 1000)
    }, [duration])

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (timerRef.current) {
                clearInterval(timerRef.current)
                timerRef.current = null
            }
        }
    }, [])

    return {
        timer,
        isRunning: timer > 0,
        start,
        stop,
        reset
    }
}
